/**
 * Onboarding anchors registry — каждый «якорный» элемент UI регистрирует
 * свой DOM-ref здесь. OnboardingFocusRing + tip-bubble читают rect и
 * позиционируются динамически — никаких хардкод-координат.
 *
 * JB 2026-05-26: «онбординг везде хуй пойми куда указывает». Раньше
 * координаты в CSS файле статичные → реальные элементы дрейфуют от
 * них при разных viewport. Теперь — single source of truth = живой DOM.
 *
 * Без Zustand — простой module-level Map + manual subscribe. Зустанд
 * требует ре-рендер на каждое обновление rect (resize/scroll), а нам
 * нужно только при подписке.
 */
import type { TipAnchor } from './types.js';

interface AnchorRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

const anchorElements = new Map<TipAnchor, HTMLElement>();

export function registerAnchor(name: TipAnchor, el: HTMLElement | null): void {
  if (el === null) {
    anchorElements.delete(name);
    return;
  }
  anchorElements.set(name, el);
}

/**
 * Получить текущий rect якоря в viewport координатах.
 * null — если якорь не зарегистрирован или элемент удалён из DOM.
 */
export function getAnchorRect(name: TipAnchor): AnchorRect | null {
  const el = anchorElements.get(name);
  if (el === undefined) return null;
  if (!el.isConnected) {
    anchorElements.delete(name);
    return null;
  }
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
  };
}

/**
 * Получить сам HTMLElement якоря — нужно для OnboardingAnchorHighlight
 * чтобы навесить `data-onboarding-focused="true"` атрибут.
 */
export function getAnchorElement(name: TipAnchor): HTMLElement | null {
  const el = anchorElements.get(name);
  if (el === undefined) return null;
  if (!el.isConnected) {
    anchorElements.delete(name);
    return null;
  }
  return el;
}
